import React, { useState, useEffect, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { useParams, useLocation } from "react-router-dom";
import { Breadcrumb } from '../Components/Breadcrumb/Breadcrumb';
import { getInvoiceById, getPlacesOfService } from "../afinanceapi";
import { getUserPracticeById, getAccountById } from "../api";
import CreateReceiptModal from "../Components/Accounts/Receipt";
import CreateInvoiceModal from "../Components/Accounts/CreateInvoice";

function Invoices() {
  const { id, accountId } = useParams(); // accountId and invoice id from URL
  const isNew = id === "1";
  const location = useLocation();

  const [invoice, setInvoice] = useState(null);
  const [account, setAccount] = useState(null);
  const [practice, setPractice] = useState([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptAccount, setReceiptAccount] = useState(null);
  const [receiptInvoiceNumber, setReceiptInvoiceNumber] = useState(null);
  const [placesOfService, setPlacesOfService] = useState([]);

  const canAccessAccounts = useMemo(() => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some(r => ['SuperAdmin', 'Finance'].includes(r));
    } catch { return false; }
  }, []);

  const breadcrumbItems = useMemo(() => {
    if (!invoice || isNew) return null;

    const invoiceLabel = `Invoice ${invoice.invoiceNumber}`;

    // PAGE-05: From statement — [upstream] > [Account Name] > Statement > Invoice [Number]
    if (location.state?.fromStatement) {
      const fi = (account?.accountFullNames || '').charAt(0);
      const accountName = location.state.accountName ?? (`Account - ${fi ? fi + ' ' : ''}${account?.accountLastName || ''}`.trim() || 'Account');
      if (!accountName) return null;
      const statementCrumbs = [
        { label: accountName, path: `/accounts/${accountId}`, canAccess: canAccessAccounts },
        { label: 'Statement', path: `/accounts/${accountId}`, canAccess: canAccessAccounts },
        { label: invoiceLabel },
      ];
      if (location.state?.fromAppointment) {
        return [
          { label: 'Appointments', path: '/bookings' },
          { label: location.state.appointmentLabel, path: location.state.appointmentPath },
          ...statementCrumbs,
        ];
      }
      if (location.state?.fromDashboard) {
        return [
          { label: 'Dashboard', path: '/home' },
          ...statementCrumbs,
        ];
      }
      return [
        { label: 'Accounts', path: '/billing', canAccess: canAccessAccounts },
        ...statementCrumbs,
      ];
    }

    // PAGE-06: From invoice list — Invoices > Invoice [Number]
    return [
      { label: 'Invoices', path: '/billing', canAccess: canAccessAccounts },
      { label: invoiceLabel },
    ];
  }, [invoice, account, accountId, isNew, location.state, canAccessAccounts]);

  useEffect(() => {
    async function getData() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) return console.error("⛔ No user token found!");

        const user = jwtDecode(token);
        const practiceData = await getUserPracticeById(user._id);
        setPractice(practiceData);

        const accountData = await getAccountById(accountId);
        if (!accountData) return console.error("⛔ No account found");
        setAccount(accountData);

        const placesOfServiceData = await getPlacesOfService();
      setPlacesOfService(placesOfServiceData);

        if (!isNew) {
          const fetchedInvoice = await getInvoiceById(id);
          setInvoice(fetchedInvoice);
        }
      } catch (error) {
        console.error("⛔ Error in invoice setup:", error);
      }
    }

    getData();
  }, [id, accountId]);

  if ((!invoice && !isNew) || !account || practice.length === 0) {
    return <p className="text-center mt-4">Loading invoice...</p>;
  }

  return (
    <div style={{ marginTop: "65px" }} className="ms-3 me-3">
      {breadcrumbItems && <Breadcrumb items={breadcrumbItems} />}
      <CreateInvoiceModal
        account={account}
        practice={practice}
        placesOfService={placesOfService}
        initialInvoice={isNew ? null : invoice}
        onOpenReceipt={(account, invoiceNumber) => {
          setReceiptAccount(account);
          setReceiptInvoiceNumber(invoiceNumber);
          setTimeout(() => setShowReceiptModal(true), 0);
        }}
        onInvoiceUpdated={() => {}}
      />

      {showReceiptModal && receiptAccount && (
        <CreateReceiptModal
          show={showReceiptModal}
          handleClose={() => setShowReceiptModal(false)}
          account={receiptAccount}
          invoiceNumber={receiptInvoiceNumber}
        />
      )}
    </div>
  );
}

export default Invoices;
